import type {
  DefaultHandlers,
  ListLocationItemsHandlerOutput,
} from "@aws-amplify/ui-react-storage/browser";

type ListLocationItemsHandler = DefaultHandlers["listLocationItems"];
type LocationItem = ListLocationItemsHandlerOutput["items"][number];

/** The most keys one S3 list request returns. */
const S3_MAX_KEYS = 1000;

/**
 * Wrap the folder listing so one page can hold more than a single S3 request
 * returns, letting the table sort a whole folder rather than a slice of it.
 *
 * S3 requests are chained until the folder runs out or `pageSize` is reached;
 * in the latter case S3's own token is passed on, so the next page carries on
 * from there.
 */
export function createFullPageListHandler(
  baseHandler: ListLocationItemsHandler,
): ListLocationItemsHandler {
  return async (input) => {
    const pageSize = input.options?.pageSize ?? S3_MAX_KEYS;
    const items: LocationItem[] = [];
    let nextToken = input.options?.nextToken;
    do {
      const output = await baseHandler({
        ...input,
        options: {
          ...input.options,
          nextToken,
          pageSize: Math.min(S3_MAX_KEYS, pageSize - items.length),
        },
      });
      items.push(...output.items);
      nextToken = output.nextToken;
    } while (nextToken && items.length < pageSize);
    return { items, nextToken };
  };
}
